import { AddBox, RemoveCircleOutline } from "@mui/icons-material";
import React, { useEffect, useState, useRef } from "react";
import { useFormContext } from "react-hook-form";
import { toast } from "react-toastify";
import newApi from "service/newApi";

function StepFilters({ next, back }) {
  const { watch, setValue } = useFormContext();
  const dataSource = watch("data_source");
  const dataSourceType = watch("data_source_type");
  const selectedClient = watch("client");

  const metadataSourceFiles = watch("metadata_source_files") || [];
  const metadataSourceData = watch("metadata_source_data");
  const metadataIsFilter = watch("metadataIsFilter");

  const folders = watch("folders");
  const metadataRecordsLimit = watch("metadata_records_limit");
  const metadataFilterGroups = watch("metadata_filter_pools") || [];
  const metadataFilters = watch("metadata_filters") || [];

  // Event Distribution configuration
  const eventDistributionColumn = watch("event_distribution_column") || "";
  const eventDistributionLimit = watch("event_distribution_limit") || "";

  const [expandedFiles, setExpandedFiles] = useState({});
  const [visibleRows, setVisibleRows] = useState({});
  const [activeTab, setActiveTab] = useState("analysis");
  const [filteredResults, setFilteredResults] = useState([]);
  const [filtersAccordionOpen, setFiltersAccordionOpen] = useState(false);
  const [expandedFolders, setExpandedFolders] = useState({});
  const [isLoadingEvents, setIsLoadingEvents] = useState(false);
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);
  const [hasViewedPreview, setHasViewedPreview] = useState(false);
  const [hasInitialFetch, setHasInitialFetch] = useState(false);
  const [apiPreviewData, setApiPreviewData] = useState(null);
  const [currentPreviewPage, setCurrentPreviewPage] = useState(1);

  const [fileMetadataPreview, setFileMetadataPreview] = useState({});
  const [loadingFileMetadata, setLoadingFileMetadata] = useState({});
  const [fileMetadataPages, setFileMetadataPages] = useState({});

  const [eventsListData, setEventsListData] = useState(null);
  const [currentEventsPage, setCurrentEventsPage] = useState(1);

  const isCopy = watch("IsCopy");
  const fetchInProgressRef = useRef(false);

  useEffect(() => {
    if (isCopy && metadataFilterGroups && metadataFilterGroups.length > 0) {
      const needsNormalization = metadataFilterGroups.some(
        (group) => !group.id,
      );

      if (needsNormalization) {
        const normalizedGroups = metadataFilterGroups.map((group, index) => ({
          ...group,
          id: group.id || Date.now() + index,
          conditions: (group.conditions || []).map((condition, condIndex) => ({
            ...condition,
            id: condition.id || Date.now() + index * 1000 + condIndex,
          })),
        }));
        setValue("metadata_filter_pools", normalizedGroups);
      }
    }
  }, [isCopy, metadataFilterGroups?.length]);

  useEffect(() => {
    if (metadataSourceFiles.length === 0 || metadataIsFilter === false) {
      next();
    }

    // if (folders?.length > 0) {
    //   getEventsSourceByFolders();
    // }
  }, [metadataSourceFiles, JSON.stringify((folders || []).sort())]);

  const getEventsSourceByFolders = async (page = 1) => {
    if (!folders || folders.length === 0) {
      toast.warning("Selecione ao menos uma pasta para buscar eventos.");
      return 0;
    }

    const folderIds = Array.isArray(folders)
      ? folders.map((f) => (typeof f === "object" ? f.id : f))
      : [];

    setIsLoadingEvents(true);
    if (page === 1) {
      toast.info(
        "Buscando eventos nas pastas selecionadas, aguarde um momento...",
        {
          autoClose: 16000,
        },
      );
    }

    try {
      const dataSourceBase =
        dataSourceType.toLowerCase() === "audio" ? "audio" : "csv";
      const requestBody = {
        folderIds: folderIds,
        type: dataSourceBase,
      };

      if (selectedClient?.id) {
        requestBody.clientId = selectedClient.id;
      }

      const { data } = await newApi.post(
        `admin/v1/events/sources/by-folders?page=${page}&limit=10&type=${dataSourceBase}`,
        requestBody,
        {
          timeout: 180000,
        },
      );

      // Check if there's any data returned
      const hasData =
        (dataSourceBase === "csv" && data?.events?.length > 0) ||
        (dataSourceBase === "audio" && data?.files?.length > 0);

      if (!data || !hasData) {
        if (page === 1) {
          toast.info("Nenhum evento encontrado nas pastas selecionadas.");
          setEventsListData(null);
          setValue("unique_events_ids", []);
          setValue("events_list_detailed", []);
          setValue("event_sources_files", []);
        }
        return 0;
      }

      // Show warning if large files detected
      if (data.warning) {
        toast.warning(data.warning, { autoClose: 8000 });
      }

      // Store pagination data
      if (page === 1) {
        setEventsListData(data);
        setCurrentEventsPage(1);
      } else {
        // Merge data based on type
        if (dataSourceBase === "csv") {
          setEventsListData((prev) => ({
            ...data,
            events: [...(prev?.events || []), ...(data.events || [])],
          }));
        } else {
          setEventsListData((prev) => ({
            ...data,
            files: [...(prev?.files || []), ...(data.files || [])],
          }));
        }
        setCurrentEventsPage(page);
      }

      // Process based on data source type
      if (dataSourceBase === "csv" && data?.events?.length > 0) {
        // For CSV: events array contains individual events
        const allEventIds = [];
        const eventsList = [];

        data.events.forEach((event) => {
          allEventIds.push(event.eventId);
          eventsList.push({
            eventId: event.eventId,
            fileName: event.sourceFileName,
            fileId: event.sourceFileId,
            folderId: event.folderId,
          });
        });

        // Update unique_events_ids with all collected event IDs
        const existingIds = watch("unique_events_ids") || [];
        const uniqueEventIds =
          page === 1
            ? [...new Set(allEventIds)]
            : [...new Set([...existingIds, ...allEventIds])];
        setValue("unique_events_ids", uniqueEventIds);

        // Update detailed events list
        const existingEventsList = watch("events_list_detailed") || [];
        const detailedEventsList =
          page === 1 ? eventsList : [...existingEventsList, ...eventsList];
        setValue("events_list_detailed", detailedEventsList);

        // Create a map of files with their event counts
        const filesMap = {};
        data.events.forEach((event) => {
          if (!filesMap[event.sourceFileId]) {
            filesMap[event.sourceFileId] = {
              id: event.sourceFileId,
              name: event.sourceFileName,
              folderId: event.folderId,
              totalEvents: 0,
            };
          }
          filesMap[event.sourceFileId].totalEvents++;
        });

        const newFiles = Object.values(filesMap);
        const existingFiles = watch("event_sources_files") || [];
        const eventSourcesFiles =
          page === 1 ? newFiles : [...existingFiles, ...newFiles];
        setValue("event_sources_files", eventSourcesFiles);

        // Show success message with pagination info
        if (page === 1) {
          const totalMsg = data.totalUniqueEvents
            ? ` (${data.totalUniqueEvents} eventos únicos no total)`
            : "";
          toast.success(`${data.events.length} eventos carregados${totalMsg}`);
        } else {
          toast.success(`Mais ${data.events.length} eventos carregados`);
        }
      } else if (dataSourceBase === "audio" && data?.files?.length > 0) {
        // For AUDIO: files array contains audio files
        const existingFiles = watch("event_sources_files") || [];
        const newFiles =
          data.files.map((f) => ({
            id: f.id,
            name: f.name,
            folderId: f.folderId,
            totalEvents: f.totalEvents || 0,
          })) || [];
        const eventSourcesFiles =
          page === 1 ? newFiles : [...existingFiles, ...newFiles];
        setValue("event_sources_files", eventSourcesFiles);

        // For audio files, each file represents one "event"
        const allEventIds = data.files.map((f) => f.id);
        const existingIds = watch("unique_events_ids") || [];
        const uniqueEventIds =
          page === 1
            ? [...new Set(allEventIds)]
            : [...new Set([...existingIds, ...allEventIds])];
        setValue("unique_events_ids", uniqueEventIds);

        // Populate events_list_detailed for the preview table
        const eventsList = data.files.map((f) => ({
          eventId: f.name,
          fileName: f.name,
          fileId: f.id,
          folderId: f.folderId,
        }));
        const existingEventsList = watch("events_list_detailed") || [];
        const detailedEventsList =
          page === 1 ? eventsList : [...existingEventsList, ...eventsList];
        setValue("events_list_detailed", detailedEventsList);

        if (page === 1) {
          toast.success(`${data.files.length} arquivos de áudio carregados`);
        } else {
          toast.success(
            `Mais ${data.files.length} arquivos de áudio carregados`,
          );
        }
      }

      // Return the number of items loaded
      return dataSourceBase === "csv"
        ? data.events?.length || 0
        : data.files?.length || 0;
    } catch (error) {
      const errorMsg =
        error?.response?.data?.message ||
        error?.message ||
        "Erro desconhecido ao buscar eventos";

      // Handle specific error types
      if (error?.response?.status === 400) {
        toast.error(`Erro na requisição: ${errorMsg}`);
      } else if (error?.response?.status === 404) {
        toast.error("Pasta ou arquivo não encontrado.");
      } else if (error?.response?.status === 422) {
        toast.error(
          "Erro ao processar arquivos CSV. Verifique o formato dos arquivos.",
        );
      } else {
        toast.error(
          "Erro ao buscar eventos nas pastas selecionadas. Tente novamente.",
        );
      }

      console.error("Error fetching events source by folders:", error);

      if (page === 1) {
        setEventsListData(null);
        setValue("unique_events_ids", []);
        setValue("events_list_detailed", []);
        setValue("event_sources_files", []);
      }

      return 0;
    } finally {
      setIsLoadingEvents(false);
    }
  };

  const getTotalMetadataFiles = () => {
    if (!metadataSourceData || !metadataSourceData.folders) return 0;
    return metadataSourceData.folders.reduce(
      (total, folder) => total + (folder.metadataFiles?.length || 0),
      0,
    );
  };

  const getUniqueColumns = () => {
    // Prioritize API columns if available
    if (apiPreviewData?.columns && apiPreviewData.columns.length > 0) {
      return apiPreviewData.columns;
    }

    if (metadataSourceData?.columns && metadataSourceData.columns.length > 0) {
      return metadataSourceData.columns;
    }

    if (!metadataSourceData?.folders) return [];

    const columnsSet = new Set();
    metadataSourceData.folders.forEach((folder) => {
      folder.metadataFiles?.forEach((file) => {
        if (file.columns && Array.isArray(file.columns)) {
          file.columns.forEach((col) => columnsSet.add(col));
        }
      });
    });

    return Array.from(columnsSet).sort();
  };

  const getTotalRecords = () => {
    if (!metadataSourceData || !metadataSourceData.folders) return 0;
    return metadataSourceData.folders.reduce(
      (total, folder) =>
        total +
        (folder.metadataFiles?.reduce(
          (sum, file) => sum + (file.totalRecords || 0),
          0,
        ) || 0),
      0,
    );
  };

  const toggleFileAccordion = (fileIndex) => {
    setExpandedFiles((prev) => ({
      ...prev,
      [fileIndex]: !prev[fileIndex],
    }));
    if (!visibleRows[fileIndex]) {
      setVisibleRows((prev) => ({ ...prev, [fileIndex]: 10 }));
    }
  };

  const toggleFolderAccordion = (folderIndex) => {
    setExpandedFolders((prev) => ({
      ...prev,
      [folderIndex]: !prev[folderIndex],
    }));
  };

  const loadMoreRows = (fileIndex) => {
    setVisibleRows((prev) => ({
      ...prev,
      [fileIndex]: (prev[fileIndex] || 10) + 10,
    }));
  };

  // Resets all preview/cache state so the API must be called fresh
  const resetPreviewState = () => {
    setHasViewedPreview(false);
    setHasInitialFetch(false);
    setApiPreviewData(null);
    setFilteredResults([]);
    setValue("metadata_filter_result_cache_key", null);
  };

  const addFilter = () => {
    const newFilter = {
      id: Date.now(),
      column: "",
      operator: "equals",
      value: "",
      limit: "",
    };
    setValue("metadata_filters", [...metadataFilters, newFilter]);
    resetPreviewState();
  };

  const removeFilter = (filterId) => {
    setValue(
      "metadata_filters",
      metadataFilters.filter((f) => f.id !== filterId),
    );
    resetPreviewState();
  };

  const updateFilter = (filterId, field, value) => {
    setValue(
      "metadata_filters",
      metadataFilters.map((f) =>
        f.id === filterId ? { ...f, [field]: value } : f,
      ),
    );
    resetPreviewState();
  };

  // Group-based filter management functions
  const addGroup = () => {
    const newGroup = {
      id: Date.now(),
      conditions: [],
      limit: "",
    };
    setValue("metadata_filter_pools", [...metadataFilterGroups, newGroup]);
    resetPreviewState();
  };

  const removeGroup = (groupId) => {
    setValue(
      "metadata_filter_pools",
      metadataFilterGroups.filter((p) => p.id !== groupId),
    );
    resetPreviewState();
  };

  const addConditionToGroup = (groupId) => {
    const newCondition = {
      id: Date.now(),
      column: "",
      operator: "equals",
      value: "",
    };
    setValue(
      "metadata_filter_pools",
      metadataFilterGroups.map((group) =>
        group.id === groupId
          ? { ...group, conditions: [...group.conditions, newCondition] }
          : group,
      ),
    );
    resetPreviewState();
  };

  const removeCondition = (groupId, conditionId) => {
    setValue(
      "metadata_filter_pools",
      metadataFilterGroups.map((group) =>
        group.id === groupId
          ? {
              ...group,
              conditions: group.conditions.filter((c) => c.id !== conditionId),
            }
          : group,
      ),
    );
    resetPreviewState();
  };

  const updateCondition = (groupId, conditionId, field, value) => {
    setValue(
      "metadata_filter_pools",
      metadataFilterGroups.map((group) =>
        group.id === groupId
          ? {
              ...group,
              conditions: group.conditions.map((c) =>
                c.id === conditionId ? { ...c, [field]: value } : c,
              ),
            }
          : group,
      ),
    );
    resetPreviewState();
  };

  const updateGroupLimit = (groupId, limit) => {
    setValue(
      "metadata_filter_pools",
      metadataFilterGroups.map((group) =>
        group.id === groupId ? { ...group, limit } : group,
      ),
    );
    resetPreviewState();
  };

  const operatorOptions = [
    { value: "equals", label: "Igual a" },
    { value: "not_equals", label: "Diferente de" },
    { value: "greater", label: "Maior que" },
    { value: "greater_or_equal", label: "Maior ou Igual a" },
    { value: "less", label: "Menor que" },
    { value: "less_or_equal", label: "Menor ou Igual" },
    { value: "contains", label: "Contém" },
    { value: "not_contains", label: "Não Contém" },
  ];

  // Check if filters or groups are configured (for enabling event distribution)
  const hasConfiguredFiltersOrGroups = () => {
    const hasValidGroups =
      metadataFilterGroups &&
      metadataFilterGroups.some(
        (group) =>
          group.conditions &&
          group.conditions.some(
            (condition) =>
              condition.column &&
              condition.column.trim() !== "" &&
              condition.operator &&
              condition.operator.trim() !== "" &&
              condition.value !== null &&
              condition.value !== undefined &&
              String(condition.value).trim() !== "",
          ),
      );

    const hasValidFilters =
      metadataFilters &&
      metadataFilters.some(
        (filter) =>
          filter.column &&
          filter.column.trim() !== "" &&
          filter.operator &&
          filter.operator.trim() !== "" &&
          filter.value !== null &&
          filter.value !== undefined &&
          String(filter.value).trim() !== "",
      );

    const hasLimit =
      metadataRecordsLimit && String(metadataRecordsLimit).trim() !== "";

    return hasValidGroups || hasValidFilters || hasLimit;
  };

  // Update event distribution values
  const updateEventDistribution = (field, value) => {
    setValue(field, value);
    resetPreviewState();
  };

  const loadMorePreviewRows = async () => {
    if (!apiPreviewData) return;

    const nextPage = apiPreviewData.pagination.currentPage + 1;
    if (nextPage > apiPreviewData.pagination.totalPages) return;

    await fetchPreviewFromAPI(nextPage);
  };

  const hasFiltersOrLimit = () => {
    // Check for valid legacy filters (all fields must be filled)
    const hasValidOldFilters =
      metadataFilters &&
      metadataFilters.some(
        (filter) =>
          filter.column &&
          filter.column.trim() !== "" &&
          filter.operator &&
          filter.operator.trim() !== "" &&
          filter.value !== null &&
          filter.value !== undefined &&
          String(filter.value).trim() !== "",
      );

    // Check for valid group filters (groups must have at least one valid condition)
    const hasValidGroupFilters =
      metadataFilterGroups &&
      metadataFilterGroups.some(
        (group) =>
          group.conditions &&
          group.conditions.some(
            (condition) =>
              condition.column &&
              condition.column.trim() !== "" &&
              condition.operator &&
              condition.operator.trim() !== "" &&
              condition.value !== null &&
              condition.value !== undefined &&
              String(condition.value).trim() !== "",
          ),
      );

    const hasLimit =
      metadataRecordsLimit && String(metadataRecordsLimit).trim() !== "";

    return hasValidOldFilters || hasValidGroupFilters || hasLimit;
  };

  const fetchPreviewFromAPI = async (page = 1) => {
    // Prevent multiple simultaneous requests
    if (fetchInProgressRef.current) {
      console.log("Request already in progress, skipping...");
      return;
    }

    try {
      fetchInProgressRef.current = true;
      setIsLoadingPreview(true);

      const folderIds = Array.isArray(folders)
        ? folders.map((f) => (typeof f === "object" ? f.id : f))
        : [];

      if (folderIds.length === 0) {
        toast.warning("Nenhuma pasta selecionada para filtrar.");
        setApiPreviewData(null);
        return;
      }

      const uniqueEventsIds = watch("unique_events_ids") || [];
      const dataSourceBase =
        dataSourceType.toLowerCase() === "audio" ? "CLOUD" : "LOCAL";

      // Build request body
      const eventSourceType =
        dataSourceType?.toLowerCase() === "audio" ? "AUDIO" : "TEXT";
      const requestBody = {
        folderIds: folderIds,
        origin: dataSource,
        eventSourceType: eventSourceType,
        page: page,
        pageSize: 10,
      };

      // Use cacheKey for pagination after first request
      if (page > 1 && apiPreviewData?.cacheKey) {
        requestBody.cacheKey = apiPreviewData.cacheKey;
      }

      // Add clientId for LOCAL origin (only for first request without cacheKey)
      if (
        dataSourceBase === "LOCAL" &&
        selectedClient?.id &&
        !requestBody.cacheKey
      ) {
        requestBody.clientId = selectedClient.id;
      }

      // Add unique event IDs for CLOUD origin with non-AUDIO source type (only for first request without cacheKey)
      // For AUDIO, uniqueEventIds are file-table UUIDs that limit results to only the paginated subset;
      // folderIds + validateEventsAgainstAudioFiles already ensure correct matching against all files.
      if (
        dataSourceBase === "CLOUD" &&
        eventSourceType !== "AUDIO" &&
        uniqueEventsIds.length > 0 &&
        !requestBody.cacheKey
      ) {
        requestBody.uniqueEventIds = uniqueEventsIds;
      }

      // Add filter groups if configured (only for first request without cacheKey)
      if (
        metadataFilterGroups &&
        metadataFilterGroups.length > 0 &&
        !requestBody.cacheKey
      ) {
        const validGroups = metadataFilterGroups
          .filter((group) => group.conditions && group.conditions.length > 0)
          .map((group) => {
            const validConditions = group.conditions.filter(
              (condition) =>
                condition.column &&
                condition.column.trim() !== "" &&
                condition.operator &&
                condition.operator.trim() !== "" &&
                condition.value &&
                condition.value.trim() !== "",
            );

            if (validConditions.length === 0) return null;

            const groupData = {
              id: group.id,
              conditions: validConditions.map((condition) => ({
                column: condition.column.trim(),
                operator: condition.operator.trim(),
                value: condition.value.trim(),
              })),
            };

            const limit = parseInt(group.limit);
            if (limit && !isNaN(limit) && limit > 0) {
              groupData.limit = limit;
            }

            return groupData;
          })
          .filter((group) => group !== null);

        if (validGroups.length > 0) {
          requestBody.filterGroups = validGroups;
        }
      } else if (
        metadataFilters &&
        metadataFilters.length > 0 &&
        !requestBody.cacheKey
      ) {
        // Add legacy filters for backward compatibility (only for first request without cacheKey)
        const validFilters = metadataFilters
          .filter(
            (filter) =>
              filter.column &&
              filter.column.trim() !== "" &&
              filter.operator &&
              filter.operator.trim() !== "" &&
              filter.value &&
              filter.value.trim() !== "",
          )
          .map((filter) => {
            const filterData = {
              column: filter.column.trim(),
              operator: filter.operator.trim(),
              value: filter.value.trim(),
            };

            const limit = parseInt(filter.limit);
            if (limit && !isNaN(limit) && limit > 0) {
              filterData.limit = limit;
            }

            return filterData;
          });

        if (validFilters.length > 0) {
          requestBody.legacyFilters = validFilters;
        }
      }

      // Add global limit (only for first request without cacheKey)
      if (!requestBody.cacheKey) {
        const globalLimit = parseInt(metadataRecordsLimit);
        if (globalLimit && !isNaN(globalLimit) && globalLimit > 0) {
          requestBody.globalLimit = globalLimit;
        }
      }

      // Add event distribution configuration (only for first request without cacheKey)
      if (!requestBody.cacheKey) {
        if (
          eventDistributionColumn &&
          eventDistributionColumn.trim() !== "" &&
          eventDistributionLimit &&
          parseInt(eventDistributionLimit) > 0
        ) {
          requestBody.eventDistribution = {
            column: eventDistributionColumn.trim(),
            eventsPerValue: parseInt(eventDistributionLimit),
          };
        }
      }

      const { data } = await newApi.post(
        "admin/v1/events/metadata/filter-preview",
        requestBody,
        { timeout: 180000 },
      );

      // Store cacheKey from first request
      if (page === 1 && data?.cacheKey) {
        setValue("metadata_filter_result_cache_key", data.cacheKey);
      }

      if (page === 1) {
        setApiPreviewData(data);
        setFilteredResults(data.records || []);
        setCurrentPreviewPage(1);
      } else {
        // Append records for pagination, preserving cacheKey from previous request
        setApiPreviewData((prev) => ({
          ...data,
          cacheKey: prev?.cacheKey || data.cacheKey,
          records: [...(prev?.records || []), ...(data.records || [])],
        }));
        setFilteredResults((prev) => [...prev, ...(data.records || [])]);
        setCurrentPreviewPage(page);
      }

      if (data.eventSourceFiles.length) {
        const newFiles =
          data.eventSourceFiles.map((f) => ({
            id: f.id,
            name: f.name,
            folderId: f.folderId,
          })) || [];

        setValue("event_sources_files", newFiles);
      }

      if (page === 1) {
        toast.success(
          `Preview atualizado: ${data.pagination.totalRecords} registros encontrados`,
        );
        setHasViewedPreview(true);
      } else {
        toast.success(`Mais ${data.records?.length || 0} registros carregados`);
      }
    } catch (error) {
      const errorMessage =
        error.response?.status === 403
          ? "Acesso negado. Você não tem permissão para visualizar estes dados."
          : "Erro ao buscar preview dos metadados filtrados. Tente novamente.";

      toast.error(errorMessage);
      console.error("Error fetching metadata filter preview:", error);
      setApiPreviewData(null);
      setFilteredResults([]);
    } finally {
      setIsLoadingPreview(false);
      fetchInProgressRef.current = false;
    }
  };

  useEffect(() => {
    if (activeTab !== "preview") {
      return;
    }

    // Prevent multiple simultaneous calls
    if (isLoadingPreview || fetchInProgressRef.current) {
      return;
    }

    // Reset pagination and fetch from API
    setCurrentPreviewPage(1);
  }, [activeTab, metadataFilters, metadataFilterGroups, metadataRecordsLimit]);

  const prepareFilteredData = () => {
    // If we have API data, we need to extract all unique event IDs from the summary
    const filteredEventIds =
      apiPreviewData?.filterSummary?.totalAfterFilters > 0
        ? [
            ...new Set(
              filteredResults.map((record) => record.EVENT_ID || record.ID),
            ),
          ]
        : [];

    const filteredFiles =
      filteredEventIds.length > 0
        ? filteredEventIds.map((eventId) => ({
            id: eventId,
            name: eventId,
          }))
        : [];

    setValue("filtered_files", filteredFiles);

    // Prepare group data for saving
    if (metadataFilterGroups.length > 0) {
      const validGroups = metadataFilterGroups
        .filter((group) => group.conditions && group.conditions.length > 0)
        .map((group) => {
          const validConditions = group.conditions
            .filter(
              (condition) =>
                condition.column &&
                condition.column.trim() !== "" &&
                condition.operator &&
                condition.operator.trim() !== "" &&
                condition.value &&
                condition.value.trim() !== "",
            )
            .map((condition) => ({
              column: condition.column.trim(),
              operator: condition.operator.trim(),
              value: condition.value.trim(),
            }));

          const groupData = {
            conditions: validConditions,
          };

          const limit = parseInt(group.limit);
          if (limit && !isNaN(limit) && limit > 0) {
            groupData.limit = limit;
          }

          return groupData;
        })
        .filter((group) => group.conditions.length > 0);

      setValue("metadata_filter_pools", validGroups);
    } else {
      // Legacy filter support
      const validMetadataFilters = metadataFilters
        .filter(
          (filter) =>
            filter.column &&
            filter.column.trim() !== "" &&
            filter.operator &&
            filter.operator.trim() !== "" &&
            filter.value &&
            filter.value.trim() !== "",
        )
        .map((filter) => {
          const filterData = {
            column: filter.column.trim(),
            operator: filter.operator.trim(),
            value: filter.value.trim(),
          };

          const limit = parseInt(filter.limit);
          if (limit && !isNaN(limit) && limit > 0) {
            filterData.limit = limit;
          }

          return filterData;
        });

      setValue("metadata_filters", validMetadataFilters);
    }

    // Save event distribution configuration if valid
    if (
      eventDistributionColumn &&
      eventDistributionColumn.trim() !== "" &&
      eventDistributionLimit &&
      parseInt(eventDistributionLimit) > 0
    ) {
      setValue("event_distribution_column", eventDistributionColumn.trim());
      setValue("event_distribution_limit", parseInt(eventDistributionLimit));
    } else {
      setValue("event_distribution_column", "");
      setValue("event_distribution_limit", "");
    }
  };

  const handleNext = () => {
    // Block proceeding if filters/distribution are configured but preview was not fetched
    // The preview API creates a cache that is required by subsequent steps
    if (hasFiltersOrLimit() && !hasViewedPreview) {
      toast.warning(
        "É necessário verificar o resultado dos filtros antes de prosseguir. Clique em 'Verificar resultado' para gerar o cache.",
        { autoClose: 5000 },
      );
      return;
    }

    // Validate that there are results if filters/groups are configured
    const hasConfiguredFilters =
      (metadataFilterGroups.length > 0 &&
        metadataFilterGroups.some(
          (group) => group.conditions && group.conditions.length > 0,
        )) ||
      metadataFilters.length > 0;

    if (hasConfiguredFilters && filteredResults.length === 0) {
      toast.warning(
        "Nenhum evento corresponde aos filtros configurados. Considere ajustar os critérios antes de continuar.",
        {
          autoClose: 5000,
        },
      );
    }

    prepareFilteredData();
    next();
  };

  const loadFileMetadataPreview = async (filename, folderId, fileKey) => {
    setLoadingFileMetadata((prev) => ({ ...prev, [fileKey]: true }));

    try {
      // Obter a página atual ou iniciar com 1
      const currentPage = fileMetadataPages[fileKey] || 1;
      const isFirstLoad = !fileMetadataPreview[fileKey];

      const requestParams = {
        filename: filename,
        folderId: folderId,
        origin: dataSource,
        page: currentPage,
        pageSize: 10,
      };

      if (dataSource === "LOCAL") {
        requestParams.clientId = selectedClient?.id;
      }

      const response = await newApi.get("v1/events/metadata/analyze", {
        params: requestParams,
        timeout: 180000,
      });

      const data = response.data;

      if (data?.folders?.[0]?.metadataFiles?.[0]) {
        const fileData = data.folders[0].metadataFiles[0];

        setFileMetadataPreview((prev) => {
          const existingData = prev[fileKey]?.data || [];
          const newData = fileData.data || [];

          return {
            ...prev,
            [fileKey]: {
              columns: fileData.columns || [],
              data: isFirstLoad ? newData : [...existingData, ...newData],
              totalRecords: fileData.totalRecords || 0,
            },
          };
        });

        // Incrementar a página para a próxima consulta
        setFileMetadataPages((prev) => ({
          ...prev,
          [fileKey]: currentPage + 1,
        }));

        if (isFirstLoad) {
          toast.success("Preview de metadados carregado com sucesso!");
        } else {
          toast.success(
            `Mais 10 registros carregados! (Página ${currentPage})`,
          );
        }
      } else {
        toast.info("Nenhum dado de preview encontrado.");
      }
    } catch (error) {
      console.error("Erro ao carregar preview de metadados:", error);
      toast.error("Erro ao carregar preview de metadados. Tente novamente.");
    } finally {
      setLoadingFileMetadata((prev) => ({ ...prev, [fileKey]: false }));
    }
  };

  const handleVerifyResult = () => {
    setActiveTab("preview");
    // Trigger the API call to create the filter cache
    setCurrentPreviewPage(1);
    setApiPreviewData(null);
    setFilteredResults([]);
    fetchInProgressRef.current = false;
    fetchPreviewFromAPI(1);
    setHasInitialFetch(true);
  };

  return (
    <div className="card bg-secondary shadow">
      <div className="card-header bg-white border-0">
        <div className="row align-items-center">
          <div className="col-8">
            <h3 className="mb-0">Filtros baseados em Metadados</h3>
          </div>
        </div>
      </div>
      <div className="card-body">
        {/* Tabs */}
        <ul className="nav nav-tabs mb-4" role="tablist">
          <li className="nav-item">
            <button
              className={`nav-link ${activeTab === "analysis" ? "active" : ""}`}
              onClick={() => setActiveTab("analysis")}
              type="button"
            >
              <i className="ni ni-chart-bar-32 mr-2"></i>
              Análise dos Metadados
            </button>
          </li>
          <li className="nav-item">
            <button
              className={`nav-link ${activeTab === "filters" ? "active" : ""}`}
              onClick={() => setActiveTab("filters")}
              type="button"
            >
              <i className="ni ni-settings-gear-65 mr-2"></i>
              Construção de Filtros
            </button>
          </li>
          <li className="nav-item">
            <button
              className={`nav-link ${activeTab === "preview" ? "active" : ""}`}
              onClick={() => {
                setActiveTab("preview");
                setHasViewedPreview(true);
              }}
              type="button"
            >
              <i className="ni ni-laptop mr-2"></i>
              Preview do Resultado
            </button>
          </li>
        </ul>

        {/* Tab Content: Analysis */}
        {activeTab === "analysis" && (
          <>
            {/* Summary Cards */}
            <div className="row mb-4">
              <div className="col-md-3">
                <div className="card bg-gradient-gray text-white">
                  <div className="card-body">
                    <div className="d-flex justify-content-between align-items-center">
                      <div>
                        <h6 className="text-uppercase text-white mb-2">
                          Arquivos de Metadata
                        </h6>
                        <h2 className="mb-0 text-white">
                          {getTotalMetadataFiles()}
                        </h2>
                      </div>
                      <div>
                        <i
                          className="ni ni-single-copy-04 text-white"
                          style={{ fontSize: "2rem" }}
                        ></i>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-md-3">
                <div className="card bg-gradient-gray text-white">
                  <div className="card-body">
                    <div className="d-flex justify-content-between align-items-center">
                      <div>
                        <h6 className="text-uppercase text-white mb-2">
                          Colunas Únicas
                        </h6>
                        <h2 className="mb-0 text-white">
                          {getUniqueColumns().length}
                        </h2>
                      </div>
                      <div>
                        <i
                          className="ni ni-chart-bar-32 text-white"
                          style={{ fontSize: "2rem" }}
                        ></i>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-md-3">
                <div className="card bg-gradient-gray text-white">
                  <div className="card-body">
                    <div className="d-flex justify-content-between align-items-center">
                      <div>
                        <h6 className="text-uppercase text-white mb-2">
                          Total de Registros
                        </h6>
                        <h2 className="mb-0 text-white">{getTotalRecords()}</h2>
                      </div>
                      <div>
                        <i
                          className="ni ni-folder-17 text-white"
                          style={{ fontSize: "2rem" }}
                        ></i>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-md-3">
                <div className="card bg-gradient-gray text-white">
                  <div className="card-body">
                    <div>
                      <h6 className="text-uppercase text-white mb-2">
                        Limite global de processamento
                      </h6>
                      <input
                        type="number"
                        className="form-control form-control-sm"
                        placeholder="Sem limite"
                        min="1"
                        value={metadataRecordsLimit || ""}
                        onChange={(e) => {
                          setValue("metadata_records_limit", e.target.value);
                          resetPreviewState();
                        }}
                        style={{
                          fontSize: "1.25rem",
                          fontWeight: "600",
                        }}
                      />
                      <small className="text-white mt-2 d-block">
                        Deixe em branco para processar todos
                      </small>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Metadata Files Accordion */}
            {metadataSourceData && metadataSourceData.folders && (
              <div className="accordion mb-4" id="metadataAccordion">
                {metadataSourceData.folders.map((folder, folderIndex) => {
                  const isFolderExpanded = expandedFolders[folderIndex];

                  return (
                    <div key={folderIndex} className="card mb-4">
                      <div
                        className="card-header bg-gradient-primary"
                        id={`folder-heading-${folderIndex}`}
                      >
                        <h5 className="mb-0">
                          <button
                            className="btn btn-link w-100 text-left d-flex justify-content-between align-items-center text-white"
                            type="button"
                            onClick={() => toggleFolderAccordion(folderIndex)}
                          >
                            <span>
                              <i
                                className={`ni ${isFolderExpanded ? "ni-bold-down" : "ni-bold-right"} mr-2`}
                              ></i>
                              <i className="ni ni-folder-17 mr-2"></i>
                              {folder.folderName}
                            </span>
                            <span className="badge">
                              {folder.metadataFiles?.length || 0} arquivo(s)
                            </span>
                          </button>
                        </h5>
                      </div>

                      {isFolderExpanded && (
                        <div className="card-body">
                          {folder.metadataFiles &&
                            folder.metadataFiles.map((file, fileIndex) => {
                              const uniqueFileIndex = `${folderIndex}-${fileIndex}`;
                              const isExpanded = expandedFiles[uniqueFileIndex];
                              const currentVisibleRows =
                                visibleRows[uniqueFileIndex] || 10;
                              const hasMoreRows =
                                file.data &&
                                file.data.length > currentVisibleRows;

                              return (
                                <div
                                  className="card mb-3"
                                  key={uniqueFileIndex}
                                >
                                  <div
                                    className="card-header"
                                    id={`heading-${uniqueFileIndex}`}
                                  >
                                    <h5 className="mb-0">
                                      <button
                                        className="btn btn-link w-100 text-left d-flex justify-content-between align-items-center"
                                        type="button"
                                        onClick={() =>
                                          toggleFileAccordion(uniqueFileIndex)
                                        }
                                      >
                                        <span>
                                          <i
                                            className={`ni ${isExpanded ? "ni-bold-down" : "ni-bold-right"} mr-2`}
                                          ></i>
                                          {file.fileName}
                                        </span>
                                        <span className="badge badge-primary badge-pill">
                                          {file.totalRecords} registros
                                        </span>
                                      </button>
                                    </h5>
                                  </div>

                                  {isExpanded && (
                                    <div className="card-body">
                                      {/* File Info */}
                                      <div className="mb-3">
                                        <p className="mb-1">
                                          <strong>Caminho:</strong>{" "}
                                          {file.filePath}
                                        </p>
                                        <p className="mb-1">
                                          <strong>Total de Colunas:</strong>{" "}
                                          {file.columns?.length || 0}
                                        </p>
                                      </div>

                                      {/* Load Preview Button */}
                                      {!fileMetadataPreview[uniqueFileIndex] &&
                                        !file.data && (
                                          <div className="mb-3">
                                            <button
                                              type="button"
                                              className="btn btn-primary btn-sm"
                                              onClick={() =>
                                                loadFileMetadataPreview(
                                                  file.fileName,
                                                  folder.folderId,
                                                  uniqueFileIndex,
                                                )
                                              }
                                              disabled={
                                                loadingFileMetadata[
                                                  uniqueFileIndex
                                                ]
                                              }
                                            >
                                              {loadingFileMetadata[
                                                uniqueFileIndex
                                              ] ? (
                                                <>
                                                  <span
                                                    className="spinner-border spinner-border-sm mr-2"
                                                    role="status"
                                                    aria-hidden="true"
                                                  ></span>
                                                  Carregando...
                                                </>
                                              ) : (
                                                <>
                                                  <i className="ni ni-chart-bar-32 mr-2"></i>
                                                  Carregar Preview (10
                                                  registros)
                                                </>
                                              )}
                                            </button>
                                          </div>
                                        )}

                                      {/* Columns Display */}
                                      <div className="mb-3">
                                        <h6 className="text-primary">
                                          Colunas:
                                        </h6>
                                        <div className="d-flex flex-wrap gap-1">
                                          {file.columns &&
                                            file.columns.map(
                                              (col, colIndex) => (
                                                <span
                                                  key={colIndex}
                                                  className="badge badge-pill"
                                                  style={{
                                                    backgroundColor:
                                                      col === "EVENT_ID"
                                                        ? "#2dce89"
                                                        : "#5e72e4",
                                                    color: "#fff",
                                                    fontSize: "0.7rem",
                                                    padding: "0.4rem 0.65rem",
                                                    marginLeft: "2px",
                                                  }}
                                                >
                                                  {col}
                                                </span>
                                              ),
                                            )}
                                        </div>
                                      </div>

                                      {/* Data Table from file.data (original) */}
                                      {file.data && file.data.length > 0 && (
                                        <div>
                                          <h6 className="text-primary mb-3">
                                            Registros:
                                          </h6>
                                          <div
                                            className="table-responsive"
                                            style={{
                                              maxHeight: "500px",
                                              overflowY: "auto",
                                            }}
                                          >
                                            <table className="table table-sm table-bordered table-striped">
                                              <thead
                                                className="thead-light"
                                                style={{
                                                  position: "sticky",
                                                  top: 0,
                                                  zIndex: 1,
                                                }}
                                              >
                                                <tr>
                                                  <th>#</th>
                                                  {file.columns &&
                                                    file.columns.map(
                                                      (col, colIndex) => (
                                                        <th
                                                          key={colIndex}
                                                          style={{
                                                            minWidth: "150px",
                                                          }}
                                                        >
                                                          {col}
                                                        </th>
                                                      ),
                                                    )}
                                                </tr>
                                              </thead>
                                              <tbody>
                                                {file.data
                                                  .slice(0, currentVisibleRows)
                                                  .map((row, rowIndex) => (
                                                    <tr key={rowIndex}>
                                                      <td>{rowIndex + 1}</td>
                                                      {file.columns &&
                                                        file.columns.map(
                                                          (col, colIndex) => (
                                                            <td key={colIndex}>
                                                              {row[col] !==
                                                                undefined &&
                                                              row[col] !== null
                                                                ? String(
                                                                    row[col],
                                                                  )
                                                                : "-"}
                                                            </td>
                                                          ),
                                                        )}
                                                    </tr>
                                                  ))}
                                              </tbody>
                                            </table>
                                          </div>
                                          {hasMoreRows && (
                                            <div className="text-center mt-3">
                                              <button
                                                type="button"
                                                className="btn btn-sm btn-outline-primary"
                                                onClick={() =>
                                                  loadMoreRows(uniqueFileIndex)
                                                }
                                              >
                                                Carregar mais 10 registros
                                              </button>
                                              <p className="text-muted mt-2 mb-0">
                                                Mostrando {currentVisibleRows}{" "}
                                                de {file.data.length} registros
                                              </p>
                                            </div>
                                          )}
                                        </div>
                                      )}

                                      {/* Data Table from preview (new) */}
                                      {fileMetadataPreview[uniqueFileIndex] && (
                                        <div>
                                          <h6 className="text-primary mb-3">
                                            Preview de Registros:
                                          </h6>
                                          <div
                                            className="table-responsive"
                                            style={{
                                              maxHeight: "500px",
                                              overflowY: "auto",
                                            }}
                                          >
                                            <table className="table table-sm table-bordered table-striped">
                                              <thead
                                                className="thead-light"
                                                style={{
                                                  position: "sticky",
                                                  top: 0,
                                                  zIndex: 1,
                                                }}
                                              >
                                                <tr>
                                                  <th>#</th>
                                                  {fileMetadataPreview[
                                                    uniqueFileIndex
                                                  ].columns &&
                                                    fileMetadataPreview[
                                                      uniqueFileIndex
                                                    ].columns.map(
                                                      (col, colIndex) => (
                                                        <th
                                                          key={colIndex}
                                                          style={{
                                                            minWidth: "150px",
                                                          }}
                                                        >
                                                          {col}
                                                        </th>
                                                      ),
                                                    )}
                                                </tr>
                                              </thead>
                                              <tbody>
                                                {fileMetadataPreview[
                                                  uniqueFileIndex
                                                ].data &&
                                                  fileMetadataPreview[
                                                    uniqueFileIndex
                                                  ].data.map(
                                                    (row, rowIndex) => (
                                                      <tr key={rowIndex}>
                                                        <td>{rowIndex + 1}</td>
                                                        {fileMetadataPreview[
                                                          uniqueFileIndex
                                                        ].columns &&
                                                          fileMetadataPreview[
                                                            uniqueFileIndex
                                                          ].columns.map(
                                                            (col, colIndex) => (
                                                              <td
                                                                key={colIndex}
                                                              >
                                                                {row[col] !==
                                                                  undefined &&
                                                                row[col] !==
                                                                  null
                                                                  ? String(
                                                                      row[col],
                                                                    )
                                                                  : "-"}
                                                              </td>
                                                            ),
                                                          )}
                                                      </tr>
                                                    ),
                                                  )}
                                              </tbody>
                                            </table>
                                          </div>
                                          <div className="d-flex justify-content-between align-items-center mt-2">
                                            <p className="text-muted mb-0">
                                              Mostrando{" "}
                                              {fileMetadataPreview[
                                                uniqueFileIndex
                                              ].data?.length || 0}{" "}
                                              de{" "}
                                              {fileMetadataPreview[
                                                uniqueFileIndex
                                              ].totalRecords || 0}{" "}
                                              registros totais
                                            </p>
                                            {fileMetadataPreview[
                                              uniqueFileIndex
                                            ].data?.length <
                                              fileMetadataPreview[
                                                uniqueFileIndex
                                              ].totalRecords && (
                                              <button
                                                type="button"
                                                className="btn btn-sm btn-outline-primary"
                                                onClick={() =>
                                                  loadFileMetadataPreview(
                                                    file.fileName,
                                                    folder.folderId,
                                                    uniqueFileIndex,
                                                  )
                                                }
                                                disabled={
                                                  loadingFileMetadata[
                                                    uniqueFileIndex
                                                  ]
                                                }
                                              >
                                                {loadingFileMetadata[
                                                  uniqueFileIndex
                                                ]
                                                  ? "Carregando..."
                                                  : "Carregar mais 10"}
                                              </button>
                                            )}
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}

        {/* Tab Content: Filters */}
        {activeTab === "filters" && (
          <div>
            <div className="mb-4">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h5 className="mb-0">Grupos de Filtros</h5>
                <button
                  type="button"
                  className="btn btn-sm btn-success"
                  onClick={addGroup}
                >
                  <AddBox
                    type="button"
                    color="inherit"
                    fontSize="medium"
                    title="Adicionar Grupo"
                    sx={{ marginRight: "8px" }}
                  />
                  Adicionar Grupo
                </button>
              </div>

              {metadataFilterGroups.length === 0 ? (
                <div className="alert alert-info" role="alert">
                  <i className="ni ni-bell-55 mr-2"></i>
                  Nenhum grupo criado. Clique em &quot;Adicionar Grupo&quot;
                  para começar a configurar filtros.
                  <br />
                  <small className="mt-2 d-block">
                    <strong>Dica:</strong> Cada grupo permite criar múltiplas
                    condições que serão avaliadas com lógica AND (todas devem
                    ser satisfeitas). Os resultados dos grupos são combinados no
                    final.
                  </small>
                </div>
              ) : (
                <div>
                  {metadataFilterGroups.map((group, groupIndex) => (
                    <div
                      key={group.id}
                      className="card mb-4 border-left-success shadow-sm"
                    >
                      <div className="card-header bg-gradient-secondary">
                        <div className="d-flex justify-content-between align-items-center">
                          <h5 className="mb-0">
                            <i className="ni ni-collection mr-2"></i>
                            Grupo #{groupIndex + 1}
                          </h5>
                          <div>
                            <button
                              type="button"
                              className="btn btn-sm btn-primary mr-2"
                              onClick={() => addConditionToGroup(group.id)}
                            >
                              <AddBox
                                fontSize="small"
                                sx={{ marginRight: "4px" }}
                              />
                              Adicionar Condição
                            </button>
                            <RemoveCircleOutline
                              type="button"
                              onClick={() => removeGroup(group.id)}
                              style={{
                                color: "red",
                                cursor: "pointer",
                                fontSize: "1.5rem",
                              }}
                              title="Remover Grupo"
                            />
                          </div>
                        </div>
                      </div>

                      <div className="card-body bg-light">
                        {/* Pool Limit */}
                        <div className="row mb-3">
                          <div className="col-md-4">
                            <div className="form-group mb-0">
                              <label className="form-control-label">
                                <i className="ni ni-chart-bar-32 mr-1"></i>
                                Limite de Registros do Grupo
                              </label>
                              <input
                                type="number"
                                className="form-control"
                                placeholder="Sem limite"
                                min="1"
                                value={group.limit || ""}
                                onChange={(e) =>
                                  updateGroupLimit(group.id, e.target.value)
                                }
                              />
                              <small className="text-muted">
                                Máximo de registros que este grupo pode retornar
                              </small>
                            </div>
                          </div>
                          <div className="col-md-8">
                            <div className="alert alert-info mb-0">
                              <strong>Lógica AND:</strong> Todas as condições
                              dentro deste grupo devem ser satisfeitas. Os
                              registros que atenderem <strong>todas</strong> as
                              condições serão incluídos no resultado final.
                            </div>
                          </div>
                        </div>

                        {/* Conditions */}
                        {group.conditions && group.conditions.length > 0 ? (
                          <div>
                            <h6 className="text-primary mb-3">
                              <i className="ni ni-settings-gear-65 mr-1"></i>
                              Condições (AND)
                            </h6>
                            {group.conditions.map((condition, condIndex) => (
                              <div
                                key={condition.id}
                                className="card mb-3 border-primary"
                              >
                                <div className="card-body">
                                  <div className="row">
                                    <div className="col-md-3">
                                      <div className="form-group mb-3">
                                        <label className="form-control-label">
                                          Coluna
                                        </label>
                                        <select
                                          className="form-control"
                                          value={condition.column}
                                          onChange={(e) =>
                                            updateCondition(
                                              group.id,
                                              condition.id,
                                              "column",
                                              e.target.value,
                                            )
                                          }
                                        >
                                          <option value="">Selecione...</option>
                                          {getUniqueColumns().map(
                                            (col, idx) => (
                                              <option key={idx} value={col}>
                                                {col}
                                              </option>
                                            ),
                                          )}
                                        </select>
                                      </div>
                                    </div>
                                    <div className="col-md-3">
                                      <div className="form-group mb-3">
                                        <label className="form-control-label">
                                          Operador
                                        </label>
                                        <select
                                          className="form-control"
                                          value={condition.operator}
                                          onChange={(e) =>
                                            updateCondition(
                                              group.id,
                                              condition.id,
                                              "operator",
                                              e.target.value.trim(),
                                            )
                                          }
                                        >
                                          {operatorOptions.map((op) => (
                                            <option
                                              key={op.value}
                                              value={op.value}
                                            >
                                              {op.label}
                                            </option>
                                          ))}
                                        </select>
                                      </div>
                                    </div>
                                    <div className="col-md-5">
                                      <div className="form-group mb-3">
                                        <label className="form-control-label">
                                          Valor
                                        </label>
                                        <input
                                          type="text"
                                          className="form-control"
                                          placeholder="Digite o valor"
                                          value={condition.value}
                                          onChange={(e) =>
                                            updateCondition(
                                              group.id,
                                              condition.id,
                                              "value",
                                              e.target.value.trim(),
                                            )
                                          }
                                        />
                                      </div>
                                    </div>
                                    <div className="col-md-1 d-flex align-items-end">
                                      <div className="form-group mt-4">
                                        <RemoveCircleOutline
                                          type="button"
                                          onClick={() =>
                                            removeCondition(
                                              group.id,
                                              condition.id,
                                            )
                                          }
                                          style={{
                                            color: "red",
                                            cursor: "pointer",
                                          }}
                                          title="Remover Condição"
                                        />
                                      </div>
                                    </div>
                                  </div>
                                  <div className="row">
                                    <div className="col-12">
                                      <small className="text-muted">
                                        {condIndex > 0 && (
                                          <strong className="text-success mr-2">
                                            E
                                          </strong>
                                        )}
                                        Condição #{condIndex + 1}:{" "}
                                        {condition.column ||
                                          "(coluna não selecionada)"}{" "}
                                        {operatorOptions.find(
                                          (op) =>
                                            op.value === condition.operator,
                                        )?.label || condition.operator}{" "}
                                        &quot;
                                        {condition.value ||
                                          "(valor não definido)"}
                                        &quot;
                                      </small>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className="alert alert-warning" role="alert">
                            <i className="ni ni-bell-55 mr-2"></i>
                            Nenhuma condição adicionada. Clique em
                            &quot;Adicionar Condição&quot; para começar.
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Legacy Filters Section (if using old format) */}
              {metadataFilters.length > 0 &&
                metadataFilterGroups.length === 0 && (
                  <div>
                    <div className="alert alert-warning mb-3">
                      <i className="ni ni-notification-70 mr-2"></i>
                      <strong>Modo de compatibilidade:</strong> Você está usando
                      o formato antigo de filtros. Considere migrar para o novo
                      sistema de grupos para ter mais flexibilidade.
                    </div>
                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <h5 className="mb-0">Filtros de Metadados (Legado)</h5>
                      <button
                        type="button"
                        className="btn btn-sm btn-primary"
                        onClick={addFilter}
                      >
                        <AddBox
                          type="button"
                          color="inherit"
                          fontSize="medium"
                          title=" Adicionar Filtro"
                          sx={{ marginRight: "8px" }}
                        />
                        Adicionar Filtro
                      </button>
                    </div>
                    <div>
                      {metadataFilters.map((filter, index) => (
                        <div
                          key={filter.id}
                          className="card mb-3 border-left-primary"
                        >
                          <div className="card-body">
                            <div className="row">
                              <div className="col-md-3">
                                <div className="form-group mb-3">
                                  <label className="form-control-label">
                                    Coluna
                                  </label>
                                  <select
                                    className="form-control"
                                    value={filter.column}
                                    onChange={(e) =>
                                      updateFilter(
                                        filter.id,
                                        "column",
                                        e.target.value,
                                      )
                                    }
                                  >
                                    <option value="">Selecione...</option>
                                    {getUniqueColumns().map((col, idx) => (
                                      <option key={idx} value={col}>
                                        {col}
                                      </option>
                                    ))}
                                  </select>
                                </div>
                              </div>
                              <div className="col-md-3">
                                <div className="form-group mb-3">
                                  <label className="form-control-label">
                                    Condição
                                  </label>
                                  <select
                                    className="form-control"
                                    value={filter.operator}
                                    onChange={(e) =>
                                      updateFilter(
                                        filter.id,
                                        "operator",
                                        e.target.value.trim(),
                                      )
                                    }
                                  >
                                    {operatorOptions.map((op) => (
                                      <option key={op.value} value={op.value}>
                                        {op.label}
                                      </option>
                                    ))}
                                  </select>
                                </div>
                              </div>
                              <div className="col-md-3">
                                <div className="form-group mb-3">
                                  <label className="form-control-label">
                                    Valor
                                  </label>
                                  <input
                                    type="text"
                                    className="form-control"
                                    placeholder="Digite o valor"
                                    value={filter.value}
                                    onChange={(e) =>
                                      updateFilter(
                                        filter.id,
                                        "value",
                                        e.target.value.trim(),
                                      )
                                    }
                                  />
                                </div>
                              </div>
                              <div className="col-md-2">
                                <div className="form-group mb-3">
                                  <label className="form-control-label">
                                    Limite
                                  </label>
                                  <input
                                    type="number"
                                    className="form-control"
                                    placeholder="Sem limite"
                                    min="1"
                                    value={filter.limit}
                                    onChange={(e) =>
                                      updateFilter(
                                        filter.id,
                                        "limit",
                                        e.target.value,
                                      )
                                    }
                                  />
                                </div>
                              </div>
                              <div className="col-md-1 d-flex align-items-end">
                                <div className="form-group mt-4">
                                  <RemoveCircleOutline
                                    type="button"
                                    onClick={() => removeFilter(filter.id)}
                                    style={{ color: "red" }}
                                  />
                                </div>
                              </div>
                            </div>
                            <div className="row">
                              <div className="col-12">
                                <small className="text-muted">
                                  Filtro #{index + 1}:{" "}
                                  {filter.column || "(coluna não selecionada)"}{" "}
                                  {operatorOptions.find(
                                    (op) => op.value === filter.operator,
                                  )?.label || filter.operator}{" "}
                                  &quot;{filter.value || "(valor não definido)"}
                                  &quot;
                                  {filter.limit &&
                                    ` - Limite: ${filter.limit} registros`}
                                </small>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
            </div>

            {/* Event Distribution Section */}
            <div className="mt-5">
              <hr className="my-4" />
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h5 className="mb-0">
                  <i className="ni ni-chart-pie-35 mr-2"></i>
                  Distribuição de Eventos
                </h5>
                {!hasConfiguredFiltersOrGroups() && (
                  <span className="badge badge-warning">
                    Configure filtros ou limites primeiro
                  </span>
                )}
              </div>

              {!hasConfiguredFiltersOrGroups() ? (
                <div className="alert alert-info" role="alert">
                  <i className="ni ni-bell-55 mr-2"></i>
                  A distribuição de eventos só pode ser configurada após definir
                  pelo menos um filtro, grupo de filtros ou limite global.
                  <br />
                  <small className="mt-2 d-block">
                    <strong>Dica:</strong> Configure seus filtros na seção acima
                    para habilitar a distribuição de eventos.
                  </small>
                </div>
              ) : (
                <div className="card border-left-info shadow-sm">
                  <div className="card-body">
                    <div className="alert alert-info mb-4">
                      <i className="ni ni-bulb-61 mr-2"></i>
                      <strong>O que é a Distribuição de Eventos?</strong>
                      <br />
                      <small>
                        Esta funcionalidade permite limitar a quantidade de
                        eventos por valor único de um campo de metadado. Por
                        exemplo, se você selecionar o campo &quot;OPERADOR&quot;
                        e definir o limite como 10, serão selecionados no máximo
                        10 eventos para cada operador único.
                      </small>
                    </div>

                    <div className="row">
                      <div className="col-md-6">
                        <div className="form-group mb-3">
                          <label className="form-control-label">
                            <i className="ni ni-single-02 mr-1"></i>
                            Campo de Metadado para Distribuição
                          </label>
                          <select
                            className="form-control"
                            value={eventDistributionColumn}
                            onChange={(e) =>
                              updateEventDistribution(
                                "event_distribution_column",
                                e.target.value,
                              )
                            }
                          >
                            <option value="">
                              Selecione um campo (opcional)
                            </option>
                            {getUniqueColumns().map((col, idx) => (
                              <option key={idx} value={col}>
                                {col}
                              </option>
                            ))}
                          </select>
                          <small className="text-muted">
                            Selecione o campo que será usado para distribuir os
                            eventos
                          </small>
                        </div>
                      </div>
                      <div className="col-md-6">
                        <div className="form-group mb-3">
                          <label className="form-control-label">
                            <i className="ni ni-chart-bar-32 mr-1"></i>
                            Eventos por Valor Único
                          </label>
                          <input
                            type="number"
                            className="form-control"
                            placeholder="Ex: 10"
                            min="1"
                            value={eventDistributionLimit}
                            onChange={(e) =>
                              updateEventDistribution(
                                "event_distribution_limit",
                                e.target.value,
                              )
                            }
                            disabled={!eventDistributionColumn}
                          />
                          <small className="text-muted">
                            {eventDistributionColumn
                              ? `Máximo de eventos para cada valor único de "${eventDistributionColumn}"`
                              : "Selecione um campo primeiro"}
                          </small>
                        </div>
                      </div>
                    </div>

                    {eventDistributionColumn && eventDistributionLimit && (
                      <div className="alert alert-success mb-0">
                        <i className="ni ni-check-bold mr-2"></i>
                        <strong>Distribuição configurada:</strong> Serão
                        selecionados no máximo{" "}
                        <strong>{eventDistributionLimit}</strong> eventos para
                        cada valor único do campo{" "}
                        <code>{eventDistributionColumn}</code>.
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Tab Content: Preview */}
        {activeTab === "preview" && (
          <div>
            {/* Load Data Button */}
            <div className="mb-4">
              <button
                type="button"
                className="btn btn-primary btn-block"
                onClick={() => {
                  if (hasFiltersOrLimit()) {
                    setCurrentPreviewPage(1);
                    setApiPreviewData(null);
                    setFilteredResults([]);
                    fetchInProgressRef.current = false;
                    fetchPreviewFromAPI(1);
                    setHasInitialFetch(true);
                  } else {
                    setCurrentEventsPage(1);
                    setEventsListData(null);
                    getEventsSourceByFolders(1);
                    setHasInitialFetch(true);
                  }
                }}
                disabled={isLoadingPreview || isLoadingEvents}
              >
                <i
                  className={`ni ${hasInitialFetch ? "ni-refresh-02" : "ni-cloud-download-95"} mr-2`}
                ></i>
                {isLoadingPreview || isLoadingEvents
                  ? "Carregando..."
                  : hasInitialFetch
                    ? hasFiltersOrLimit()
                      ? "Recarregar Registros Filtrados"
                      : "Recarregar Lista de Eventos"
                    : hasFiltersOrLimit()
                      ? "Buscar Registros Filtrados (10 registros)"
                      : "Carregar Lista de Eventos (10 eventos)"}
              </button>
              <small className="text-muted d-block mt-2 text-center">
                {hasFiltersOrLimit()
                  ? "Filtros ou limites detectados. Serão carregados 10 registros filtrados."
                  : "Nenhum filtro configurado. Serão carregados 10 eventos das pastas selecionadas."}
              </small>
            </div>

            {/* Preview Loading Indicator */}
            {isLoadingPreview && (
              <div
                className="d-flex justify-content-center align-items-center mb-4 p-4"
                style={{
                  backgroundColor: "#f8f9fa",
                  borderRadius: "8px",
                  border: "1px solid #dee2e6",
                }}
              >
                <div className="spinner-border text-primary mr-3" role="status">
                  <span className="sr-only">Carregando...</span>
                </div>
                <div>
                  <h5 className="mb-1">
                    Processando resultados dos filtros...
                  </h5>
                  <p
                    className="mb-0 text-muted"
                    style={{ fontSize: "0.85rem" }}
                  >
                    Aguarde enquanto aplicamos os filtros e limites
                    configurados.
                  </p>
                </div>
              </div>
            )}

            {/* Loading Indicator */}
            {!isLoadingPreview && isLoadingEvents && (
              <div
                className="d-flex justify-content-center align-items-center mb-4 p-4"
                style={{
                  backgroundColor: "#f8f9fa",
                  borderRadius: "8px",
                  border: "1px solid #dee2e6",
                }}
              >
                <div className="spinner-border text-primary mr-3" role="status">
                  <span className="sr-only">Carregando...</span>
                </div>
                <div>
                  <h5 className="mb-1">
                    Buscando eventos nas pastas selecionadas...
                  </h5>
                  <p
                    className="mb-0 text-muted"
                    style={{ fontSize: "0.85rem" }}
                  >
                    Este processo pode levar alguns segundos. Por favor aguarde.
                  </p>
                </div>
              </div>
            )}

            {/* Applied Filters Summary */}
            {!isLoadingPreview &&
              (() => {
                // Filter only valid groups with valid conditions
                const validGroups = metadataFilterGroups
                  .map((group) => {
                    if (!group.conditions || group.conditions.length === 0)
                      return null;

                    const validConditions = group.conditions.filter(
                      (condition) =>
                        condition.column &&
                        condition.column.trim() !== "" &&
                        condition.operator &&
                        condition.operator.trim() !== "" &&
                        condition.value &&
                        condition.value.trim() !== "",
                    );

                    if (validConditions.length === 0) return null;

                    return { ...group, conditions: validConditions };
                  })
                  .filter((group) => group !== null);

                return validGroups.length > 0 ? (
                  <div className="card mb-4">
                    <div
                      className="card-header bg-gradient-secondary"
                      style={{ cursor: "pointer" }}
                      onClick={() =>
                        setFiltersAccordionOpen(!filtersAccordionOpen)
                      }
                    >
                      <h5 className="mb-0 d-flex justify-content-between align-items-center">
                        <span>
                          <i
                            className={`ni ${filtersAccordionOpen ? "ni-bold-down" : "ni-bold-right"} mr-2`}
                          ></i>
                          Grupos Aplicados{" "}
                          <span className="badge badge-success badge-pill">
                            {validGroups.length}
                          </span>
                        </span>
                      </h5>
                    </div>
                    {filtersAccordionOpen && (
                      <div className="card-body">
                        {validGroups.map((group, groupIndex) => (
                          <div key={group.id} className="mb-4">
                            <h6 className="text-success">
                              <i className="ni ni-collection mr-2"></i>
                              Grupo #{groupIndex + 1}
                              {group.limit && (
                                <span className="badge badge-info ml-2">
                                  Limite: {group.limit}
                                </span>
                              )}
                            </h6>
                            <div className="row">
                              {group.conditions.map((condition, condIndex) => (
                                <div
                                  key={condition.id || condIndex}
                                  className="col-md-6 mb-2"
                                >
                                  <div
                                    className="alert alert-primary mb-2"
                                    style={{ fontSize: "0.9rem" }}
                                  >
                                    {condIndex > 0 && (
                                      <strong className="text-success mr-2">
                                        E
                                      </strong>
                                    )}
                                    <strong>Condição #{condIndex + 1}:</strong>{" "}
                                    <code className="text-white ml-2 mr-2 bg-gradient-gray p-1 rounded-sm">
                                      {condition.column}
                                    </code>{" "}
                                    <strong>
                                      {operatorOptions.find(
                                        (op) => op.value === condition.operator,
                                      )?.label || condition.operator}
                                    </strong>{" "}
                                    <code className="text-white ml-2 mr-2 bg-gradient-gray p-1 rounded-sm">
                                      &quot;{condition.value}&quot;
                                    </code>
                                  </div>
                                </div>
                              ))}
                            </div>
                            <hr />
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ) : null;
              })()}

            {/* Legacy Applied Filters Summary */}
            {!isLoadingPreview &&
              (() => {
                // Filter only valid legacy filters
                const validFilters = metadataFilters.filter(
                  (filter) =>
                    filter.column &&
                    filter.column.trim() !== "" &&
                    filter.operator &&
                    filter.operator.trim() !== "" &&
                    filter.value &&
                    filter.value.trim() !== "",
                );

                return (
                  validFilters.length > 0 &&
                  metadataFilterGroups.length === 0 && (
                    <div className="card mb-4">
                      <div
                        className="card-header bg-gradient-secondary"
                        style={{ cursor: "pointer" }}
                        onClick={() =>
                          setFiltersAccordionOpen(!filtersAccordionOpen)
                        }
                      >
                        <h5 className="mb-0 d-flex justify-content-between align-items-center">
                          <span>
                            <i
                              className={`ni ${filtersAccordionOpen ? "ni-bold-down" : "ni-bold-right"} mr-2`}
                            ></i>
                            Filtros Aplicados{" "}
                            <span className="badge badge-primary badge-pill">
                              {validFilters.length}
                            </span>
                          </span>
                        </h5>
                      </div>
                      {filtersAccordionOpen && (
                        <div className="card-body">
                          <div className="row">
                            {validFilters.map((filter, index) => (
                              <div key={filter.id} className="col-md-6 mb-2">
                                <div
                                  className="alert alert-primary mb-2"
                                  style={{ fontSize: "1rem" }}
                                >
                                  <strong>Filtro #{index + 1}:</strong>{" "}
                                  <code className="text-white ml-2 mr-2 bg-gradient-gray p-1 rounded-sm">
                                    {filter.column}
                                  </code>{" "}
                                  <strong>
                                    {operatorOptions.find(
                                      (op) => op.value === filter.operator,
                                    )?.label || filter.operator}
                                  </strong>{" "}
                                  <code className="text-white ml-2 mr-2 bg-gradient-gray p-1 rounded-sm">
                                    &quot;{filter.value}&quot;
                                  </code>
                                  {filter.limit && (
                                    <>
                                      {" "}
                                      -{" "}
                                      <em>Limite: {filter.limit} registros</em>
                                    </>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )
                );
              })()}

            {/* Event Distribution Summary */}
            {!isLoadingPreview &&
              eventDistributionColumn &&
              eventDistributionLimit && (
                <div className="card mb-4">
                  <div className="card-header bg-gradient-info">
                    <h5 className="mb-0 text-white">
                      <i className="ni ni-chart-pie-35 mr-2"></i>
                      Distribuição de Eventos Configurada
                    </h5>
                  </div>
                  <div className="card-body">
                    <div className="row">
                      <div className="col-md-6">
                        <p className="mb-0">
                          <strong>Campo de Distribuição:</strong>{" "}
                          <code>{eventDistributionColumn}</code>
                        </p>
                      </div>
                      <div className="col-md-6">
                        <p className="mb-0">
                          <strong>Eventos por Valor:</strong>{" "}
                          <span className="badge badge-info">
                            {eventDistributionLimit}
                          </span>
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

            {/* Events List (when no filters) */}
            {!isLoadingPreview &&
              !isLoadingEvents &&
              eventsListData &&
              !hasFiltersOrLimit() &&
              (eventsListData.files?.length > 0 ||
                eventsListData.events?.length > 0) && (
                <div className="card mb-4">
                  <div className="card-header">
                    <h5 className="mb-0">
                      <i className="ni ni-bullet-list-67 mr-2"></i>
                      Lista de Eventos
                    </h5>
                  </div>
                  <div className="card-body">
                    {/* Summary cards */}
                    {eventsListData &&
                      (eventsListData.files?.length > 0 ||
                        eventsListData.events?.length > 0) && (
                        <div className="row mb-3">
                          <div className="col-md-6">
                            <div className="card bg-gradient-primary text-white">
                              <div className="card-body py-3">
                                <div className="d-flex justify-content-between align-items-center">
                                  <div>
                                    <h6 className="text-uppercase text-white mb-1">
                                      {eventsListData.events
                                        ? "Eventos Carregados"
                                        : "Total de Arquivos"}
                                    </h6>
                                    <h3 className="mb-0 text-white">
                                      {eventsListData.events?.length ||
                                        eventsListData.files?.length ||
                                        0}
                                    </h3>
                                  </div>
                                  <div>
                                    <i
                                      className="ni ni-single-copy-04 text-white"
                                      style={{ fontSize: "2rem" }}
                                    ></i>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                          <div className="col-md-6">
                            <div className="card bg-gradient-success text-white">
                              <div className="card-body py-3">
                                <div className="d-flex justify-content-between align-items-center">
                                  <div>
                                    <h6 className="text-uppercase text-white mb-1">
                                      Event IDs Únicos
                                    </h6>
                                    <h3 className="mb-0 text-white">
                                      {eventsListData.totalUniqueEvents ||
                                        (watch("unique_events_ids") || [])
                                          .length}
                                    </h3>
                                  </div>
                                  <div>
                                    <i
                                      className="ni ni-tag text-white"
                                      style={{ fontSize: "2rem" }}
                                    ></i>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}

                    <div
                      className="table-responsive"
                      style={{ maxHeight: "600px", overflowY: "auto" }}
                    >
                      <table className="table table-sm table-hover table-striped">
                        <thead
                          className="thead-light"
                          style={{
                            position: "sticky",
                            top: 0,
                            zIndex: 10,
                            backgroundColor: "#f6f9fc",
                          }}
                        >
                          <tr>
                            <th style={{ backgroundColor: "#f6f9fc" }}>#</th>
                            <th style={{ backgroundColor: "#f6f9fc" }}>
                              Event ID
                            </th>
                            <th style={{ backgroundColor: "#f6f9fc" }}>
                              Nome do Arquivo
                            </th>
                            <th style={{ backgroundColor: "#f6f9fc" }}>
                              Folder ID
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {(watch("events_list_detailed") || []).map(
                            (event, index) => (
                              <tr
                                key={`${event.eventId}-${event.fileId}-${index}`}
                              >
                                <td>{index + 1}</td>
                                <td>
                                  <code>{event.eventId}</code>
                                </td>
                                <td>
                                  <strong>{event.fileName}</strong>
                                </td>
                                <td>
                                  <small className="text-muted">
                                    {event.folderId}
                                  </small>
                                </td>
                              </tr>
                            ),
                          )}
                        </tbody>
                      </table>
                    </div>
                    <div className="d-flex justify-content-between align-items-center mt-3">
                      <p className="text-muted mb-0">
                        Mostrando {(watch("events_list_detailed") || []).length}{" "}
                        eventos
                        {eventsListData.totalUniqueEvents && (
                          <span>
                            {" "}
                            de {eventsListData.totalUniqueEvents} únicos no
                            total
                          </span>
                        )}
                        {eventsListData.page && eventsListData.totalPages && (
                          <span className="ml-2">
                            (Página {eventsListData.page} de{" "}
                            {eventsListData.totalPages})
                          </span>
                        )}
                      </p>
                      {eventsListData.page &&
                        eventsListData.totalPages &&
                        eventsListData.page < eventsListData.totalPages && (
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-primary"
                            onClick={() =>
                              getEventsSourceByFolders(currentEventsPage + 1)
                            }
                            disabled={isLoadingEvents}
                          >
                            {isLoadingEvents
                              ? "Carregando..."
                              : "Carregar mais 10"}
                          </button>
                        )}
                    </div>
                  </div>
                </div>
              )}

            {/* No Events Found Warning */}
            {!isLoadingPreview &&
              !isLoadingEvents &&
              (watch("unique_events_ids") || []).length === 0 &&
              !eventsListData &&
              fileMetadataPreview.length === 0 && (
                <div
                  className="alert alert-danger d-flex align-items-center"
                  role="alert"
                >
                  <i
                    className="ni ni-fat-remove mr-3"
                    style={{ fontSize: "2rem" }}
                  ></i>
                  <div>
                    <h5 className="mb-1">Nenhum evento encontrado</h5>
                    <p className="mb-0">
                      Não foram encontrados eventos nas pastas selecionadas com
                      a configuração atual. Verifique se as pastas contêm
                      eventos válidos ou ajuste os critérios de busca.
                    </p>
                  </div>
                </div>
              )}

            {/* Results Summary */}
            {!isLoadingPreview &&
              apiPreviewData?.pagination?.totalRecords > 0 && (
                <div className="row mb-4">
                  <div className="col-md-3">
                    <div className="card bg-gradient-info text-white">
                      <div className="card-body">
                        <div className="d-flex justify-content-between align-items-center">
                          <div>
                            <h6 className="text-uppercase text-white mb-2">
                              Total de Registros Origem
                            </h6>
                            <h2 className="mb-0 text-white">
                              {apiPreviewData.filterSummary
                                ?.totalSourceRecords || 0}
                            </h2>
                          </div>
                          <div>
                            <i
                              className="ni ni-archive-2 text-white"
                              style={{ fontSize: "2rem" }}
                            ></i>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-3">
                    <div className="card bg-gradient-primary text-white">
                      <div className="card-body">
                        <div className="d-flex justify-content-between align-items-center">
                          <div>
                            <h6 className="text-uppercase text-white mb-2">
                              Eventos Correspondentes
                            </h6>
                            <h2 className="mb-0 text-white">
                              {apiPreviewData.filterSummary
                                ?.totalMatchingEventIds || 0}
                            </h2>
                          </div>
                          <div>
                            <i
                              className="ni ni-bullet-list-67 text-white"
                              style={{ fontSize: "2rem" }}
                            ></i>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-3">
                    <div className="card bg-gradient-success text-white">
                      <div className="card-body">
                        <div className="d-flex justify-content-between align-items-center">
                          <div>
                            <h6 className="text-uppercase text-white mb-2">
                              Após Filtros
                            </h6>
                            <h2 className="mb-0 text-white">
                              {apiPreviewData.pagination?.totalRecords || 0}
                            </h2>
                          </div>
                          <div>
                            <i
                              className="ni ni-check-bold text-white"
                              style={{ fontSize: "2rem" }}
                            ></i>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-3">
                    <div className="card bg-gradient-warning text-white">
                      <div className="card-body">
                        <div className="d-flex justify-content-between align-items-center">
                          <div>
                            <h6 className="text-uppercase text-white mb-2">
                              Taxa de Correspondência
                            </h6>
                            <h2 className="mb-0 text-white">
                              {apiPreviewData.filterSummary
                                ?.totalSourceRecords > 0
                                ? (
                                    (apiPreviewData.pagination.totalRecords /
                                      apiPreviewData.filterSummary
                                        .totalSourceRecords) *
                                    100
                                  ).toFixed(1)
                                : 0}
                              %
                            </h2>
                          </div>
                          <div>
                            <i
                              className="ni ni-chart-pie-35 text-white"
                              style={{ fontSize: "2rem" }}
                            ></i>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

            {/* Results Table */}
            {!isLoadingPreview &&
            hasFiltersOrLimit() &&
            filteredResults.length > 0 ? (
              <div className="card">
                <div className="card-header">
                  <h5 className="mb-0">
                    <i className="ni ni-archive-2 mr-2"></i>
                    Registros Filtrados
                  </h5>
                </div>
                <div className="card-body">
                  <div
                    className="table-responsive"
                    style={{ maxHeight: "600px", overflowY: "auto" }}
                  >
                    <table className="table table-sm table-hover table-striped">
                      <thead
                        className="thead-light"
                        style={{
                          position: "sticky",
                          top: 0,
                          zIndex: 10,
                          backgroundColor: "#f6f9fc",
                        }}
                      >
                        <tr>
                          {/* <th>#</th> */}
                          <th style={{ backgroundColor: "#f6f9fc" }}>ID</th>
                          {/* <th>Arquivo</th> */}
                          <th style={{ backgroundColor: "#f6f9fc" }}>Pasta</th>
                          {getUniqueColumns()
                            .filter((col) => col !== "EVENT_ID" && col !== "ID")
                            .slice(0, 10)
                            .map((col, idx) => (
                              <th
                                key={idx}
                                style={{ backgroundColor: "#f6f9fc" }}
                              >
                                {col}
                              </th>
                            ))}
                        </tr>
                      </thead>
                      <tbody>
                        {filteredResults.map((record, index) => (
                          <tr key={index}>
                            {/* <td>{index + 1}</td> */}
                            <td>
                              <code>{record.EVENT_ID || record.ID}</code>
                            </td>
                            {/* <td>
                              <small>{record._metadata?.fileName}</small>
                            </td> */}
                            <td>
                              <small>{record._metadata?.folderName}</small>
                            </td>
                            {getUniqueColumns()
                              .filter(
                                (col) => col !== "EVENT_ID" && col !== "ID",
                              )
                              .slice(0, 10)
                              .map((col, idx) => (
                                <td key={idx}>
                                  <small>
                                    {record[col] !== undefined &&
                                    record[col] !== null
                                      ? String(record[col])
                                      : "-"}
                                  </small>
                                </td>
                              ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {apiPreviewData?.pagination?.hasNextPage && (
                    <div className="text-center mt-3">
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-primary"
                        onClick={loadMorePreviewRows}
                        disabled={isLoadingPreview}
                      >
                        {isLoadingPreview
                          ? "Carregando..."
                          : "Carregar mais 10 registros"}
                      </button>
                      <p className="text-muted mt-2 mb-0">
                        Mostrando {filteredResults.length} de{" "}
                        {apiPreviewData.pagination.totalRecords} registros
                        {" (Página "}
                        {apiPreviewData.pagination.currentPage} de{" "}
                        {apiPreviewData.pagination.totalPages})
                      </p>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              !isLoadingPreview &&
              hasFiltersOrLimit() && (
                <div className="alert alert-warning" role="alert">
                  <i className="ni ni-bell-55 mr-2"></i>
                  Nenhum registro corresponde aos filtros/grupos aplicados.
                  Tente ajustar os critérios de filtro.
                </div>
              )
            )}
          </div>
        )}

        <div>
          <div className="d-flex justify-content-between gap-1">
            <button type="button" className="btn btn-secondary" onClick={back}>
              Voltar
            </button>
            {!hasFiltersOrLimit() && (
              <button
                type="button"
                className="btn btn-outline-primary"
                onClick={next}
                disabled={isLoadingEvents || isLoadingPreview}
              >
                Pular
              </button>
            )}
            {hasFiltersOrLimit() && !hasViewedPreview ? (
              <button
                type="button"
                className="btn btn-warning"
                onClick={handleVerifyResult}
                disabled={isLoadingEvents || isLoadingPreview}
              >
                <i className="ni ni-laptop mr-2"></i>
                Verificar resultado
              </button>
            ) : (
              <button
                type="button"
                className="btn btn-success"
                onClick={handleNext}
                disabled={isLoadingEvents || isLoadingPreview}
              >
                Próximo
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default StepFilters;
